import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/hooks/useSession';
import { Wordmark } from './Wordmark';
import { Icono } from './iconos';

// Shell del panel según el prototipo iter5: sidebar 264px (lockup + 14 ámbitos,
// los no operativos inactivos en gris, sin etiqueta "pronto" — identity.md) +
// topbar 62px (pill "Bot activo", usuario, Salir). El estado del bot es
// estático en v1. El título de la topbar refleja la pantalla activa.

const TITULOS: Record<string, { titulo: string; sub: string }> = {
  '/atencion': {
    titulo: 'Atención telefónica',
    sub: 'Llamadas atendidas por el bot en tiempo real',
  },
  '/cuadro': {
    titulo: 'Cuadro de mando',
    sub: 'Resumen de atención telefónica · bot de voz IA',
  },
};

const NAV: {
  grupo: string;
  items: { label: string; icono: string; to?: string }[];
}[] = [
  {
    grupo: 'Panel',
    items: [
      { label: 'Atención telefónica', icono: 'phone', to: '/atencion' },
      { label: 'Cuadro de mando', icono: 'chart', to: '/cuadro' },
      { label: 'Visión de plataforma', icono: 'grid' },
    ],
  },
  {
    grupo: 'Operación',
    items: [
      { label: 'SAT y averías', icono: 'wrench' },
      { label: 'Mantenimiento', icono: 'cal' },
      { label: 'Técnicos y movilidad', icono: 'pin' },
      { label: 'Almacén y compras', icono: 'box' },
    ],
  },
  {
    grupo: 'Comercial',
    items: [
      { label: 'Leads y CRM', icono: 'users' },
      { label: 'Análisis de ofertas', icono: 'doc' },
      { label: 'Marketing', icono: 'megaphone' },
    ],
  },
  {
    grupo: 'Gestión',
    items: [
      { label: 'Administración y finanzas', icono: 'euro' },
      { label: 'Documentación y normativa', icono: 'book' },
      { label: 'RRHH y conocimiento', icono: 'badge' },
    ],
  },
  {
    grupo: 'Inteligencia',
    items: [
      { label: 'Cliente y experiencia', icono: 'smile' },
      { label: 'Dirección y datos', icono: 'trend' },
      { label: 'IA imagen, voz y documentos', icono: 'sparkle' },
    ],
  },
];

export function Layout({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const { pathname } = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const email = session?.user.email ?? '';
  const inicial = (email[0] ?? '?').toUpperCase();
  // ¿Es el cuadro? Con el mismo criterio que el router, que es quien decide qué se
  // pinta: sin distinguir mayúsculas, con o sin barra final y con la dirección
  // decodificada (/Cuadro/ y /cuadr%6F también lo son). Una comparación a mano de
  // `pathname` se quedaba corta en los dos últimos casos.
  const enCuadro = useMatch('/cuadro') !== null;
  const { titulo, sub } = TITULOS[enCuadro ? '/cuadro' : '/atencion']!;
  // La vista de impresión es la del informe del cuadro (change 003), y solo ahí.
  // Atención se sigue imprimiendo como siempre, y con Ctrl+P sale con teléfonos: D7
  // gobierna las descargas del cuadro, y esa impresión equivale a una captura de
  // quien ya ve esos datos (aceptado por Sergio el 17-sep, AD-P2-4).
  const imprimible = enCuadro;

  // al navegar, el drawer móvil se cierra
  useEffect(() => {
    setMenuAbierto(false);
  }, [pathname]);

  return (
    <div
      className={`grid h-screen overflow-hidden grid-cols-1 grid-rows-[62px_1fr] lg:grid-cols-[264px_1fr] ${
        imprimible ? 'print:block print:h-auto print:overflow-visible' : ''
      }`}
    >
      {/* backdrop del drawer móvil */}
      {menuAbierto && (
        <div
          className="fixed inset-0 z-30 bg-[var(--navy)]/40 lg:hidden"
          aria-hidden="true"
          onClick={() => setMenuAbierto(false)}
        />
      )}
      <aside
        data-testid="sidebar"
        className={`${
          menuAbierto
            ? 'fixed inset-y-0 left-0 z-40 flex w-[264px] shadow-[var(--shadow)]'
            : 'hidden'
        } lg:static lg:z-auto lg:row-span-2 lg:flex lg:w-auto flex-col overflow-y-auto bg-[var(--surface)] border-r border-[var(--border)] px-3.5 pt-5 pb-3.5 ${
          imprimible ? 'print:hidden' : ''
        }`}
      >
        <div className="flex flex-col px-2 pb-4 mb-1.5 border-b border-[var(--border-soft)]">
          <span className="inline-flex w-fit flex-col">
            <Wordmark className="block h-5 w-auto text-[var(--orange)]" />
            <span className="mt-2 pt-2 border-t border-[var(--border)] text-[9px] font-semibold uppercase tracking-[0.218em] text-[var(--text-2)] whitespace-nowrap leading-none">
              Centro de Atención IA
            </span>
          </span>
        </div>

        <nav className="mt-1.5 flex flex-col gap-0.5">
          {NAV.map(({ grupo, items }) => (
            <div key={grupo}>
              <div className="px-2.5 pt-3.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-mute)]">
                {grupo}
              </div>
              {items.map((item) =>
                item.to ? (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    className={({ isActive }) =>
                      isActive
                        ? "relative flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-[13px] font-semibold bg-[var(--orange-bg)] text-[var(--navy)] before:content-[''] before:absolute before:-left-3.5 before:top-[7px] before:bottom-[7px] before:w-[3px] before:rounded-r-[3px] before:bg-[var(--orange)]"
                        : 'flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icono
                          nombre={item.icono}
                          className={`w-[18px] h-[18px] shrink-0 ${isActive ? 'text-[var(--orange)]' : 'text-[var(--text-dim)]'}`}
                        />
                        <span className="min-w-0 flex-1 leading-tight">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ) : (
                  <span
                    key={item.label}
                    className="flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-[13px] font-medium text-[var(--text-mute)] cursor-default"
                  >
                    <Icono
                      nombre={item.icono}
                      className="w-[18px] h-[18px] shrink-0 text-[var(--text-mute)] opacity-70"
                    />
                    <span className="min-w-0 flex-1 leading-tight">{item.label}</span>
                  </span>
                )
              )}
            </div>
          ))}
        </nav>

        <div className="mt-auto border-t border-[var(--border-soft)] px-3 py-3 text-[11.5px] leading-relaxed text-[var(--text-dim)]">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--status-ok)] shadow-[0_0_0_3px_var(--status-ok-halo)]" />
            Bot de voz operativo
          </div>
          <div className="mt-2 text-[11px] text-[var(--text-mute)]">Aramática · Siwell</div>
        </div>
      </aside>

      <header
        className={`col-start-1 lg:col-start-2 z-[5] flex items-center gap-2.5 lg:gap-3.5 bg-[var(--surface)] border-b border-[var(--border)] px-3.5 lg:px-[22px] ${
          imprimible ? 'print:hidden' : ''
        }`}
      >
        <button
          type="button"
          aria-label="Abrir menú"
          aria-expanded={menuAbierto}
          onClick={() => setMenuAbierto(true)}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)] lg:hidden cursor-pointer"
        >
          <Icono nombre="list" className="h-[18px] w-[18px]" />
        </button>
        <div className="min-w-0">
          <h1 className="m-0 truncate text-[15.5px] font-semibold tracking-[-0.1px]">{titulo}</h1>
          <div className="mt-px hidden truncate text-[12.5px] text-[var(--text-dim)] sm:block">
            {sub}
          </div>
        </div>
        <div className="flex-1" />
        <span className="hidden items-center gap-2 rounded-full border border-[var(--orange-border)] bg-[var(--orange-bg)] py-1.5 pl-[11px] pr-3 text-xs font-semibold text-[var(--orange-ink)] sm:flex">
          <span className="h-[7px] w-[7px] rounded-full bg-[var(--orange)] animate-[blip_1.7s_infinite]" />
          Bot activo
        </span>
        <div className="flex items-center gap-2.5 border-l border-[var(--border-soft)] pl-2.5 lg:pl-3 text-[12.5px] font-medium text-[var(--text-2)]">
          <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg bg-[var(--navy)] text-xs font-semibold text-white">
            {inicial}
          </span>
          <span className="hidden max-w-[180px] truncate md:inline">{email}</span>
        </div>
        <button
          type="button"
          aria-label="Salir"
          onClick={() => void supabase.auth.signOut()}
          className="flex shrink-0 items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[12.5px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--navy)] cursor-pointer"
        >
          <Icono nombre="logout" className="w-4 h-4 shrink-0" />
          <span className="hidden sm:inline">Salir</span>
        </button>
      </header>

      <main
        className={`col-start-1 lg:col-start-2 overflow-auto px-3.5 pt-4 pb-6 lg:px-[22px] lg:pt-5 lg:pb-[26px] ${
          imprimible ? 'print:overflow-visible print:p-0' : ''
        }`}
      >
        {children}
      </main>
    </div>
  );
}
